import React, { useState, useEffect } from 'react';
import { useCountries, useProvinces, useCities } from '../../../hooks/useLocations';
import type { CustomerAddressDto, CreateCustomerAddressDto, UpdateCustomerAddressDto } from '../../../types/locations';
import { Button } from '@/components/ui/button';
import { Loader2, AlertCircle, Map as MapIcon, ChevronsUpDown, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { LocationMap } from '../../../components/LocationMap';
import { LocationCombobox } from '../../../components/LocationCombobox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface CustomerAddressFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  address?: CustomerAddressDto | Partial<CustomerAddressDto> | null;
  onSave: (data: any) => void; // Emits address data to parent
}

export const CustomerAddressForm: React.FC<CustomerAddressFormProps> = ({
  isOpen,
  onOpenChange,
  address,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    addressLine: '',
    postalCode: '',
    cityId: '',
    cityName: '',
    provinceName: '',
    countryName: '',
    isDefault: false,
    isActive: true,
  });

  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const [selectedProvinceId, setSelectedProvinceId] = useState<string>('');

  // React Query Hooks (Data only)
  const { data: countries = [], isLoading: loadingCountries } = useCountries();
  const { data: provinces = [], isLoading: loadingProvinces } = useProvinces(selectedCountryId || null);
  const { data: cities = [], isLoading: loadingCities } = useCities(selectedProvinceId || null);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (address) {
        setFormData({
          name: address.name || '',
          addressLine: address.addressLine || '',
          postalCode: address.postalCode || '',
          cityId: address.cityId || '',
          cityName: address.cityName || '',
          provinceName: address.provinceName || '',
          countryName: address.countryName || '',
          isDefault: address.isDefault || false,
          isActive: address.isActive ?? true,
        });
      } else {
        resetForm();
      }
      setError(null);
    }
  }, [isOpen, address]);

  // Set country when dialog opens or countries load
  useEffect(() => {
    if (isOpen && address && countries.length > 0) {
      const country = countries.find(c => c.name === address.countryName);
      if (country) {
        setSelectedCountryId(country.id);
      } else if (!selectedCountryId) {
        // Fallback to Spain for new or if country not found
        const spain = countries.find(c => c.isoCode === 'ES');
        if (spain) setSelectedCountryId(spain.id);
      }
    } else if (isOpen && !address && countries.length > 0 && !selectedCountryId) {
        // Default to Spain for new address
        const spain = countries.find(c => c.isoCode === 'ES');
        if (spain) setSelectedCountryId(spain.id);
    }
  }, [isOpen, address, countries, selectedCountryId]);

  // Set province when provinces load
  useEffect(() => {
    if (isOpen && address && provinces.length > 0 && selectedCountryId) {
        // Only set if not already matched or if we are resetting
        const province = provinces.find(p => p.name === address.provinceName);
        if (province && selectedProvinceId !== province.id) {
            setSelectedProvinceId(province.id);
        }
    }
  }, [isOpen, address, provinces, selectedCountryId]);

  const resetForm = () => {
    setFormData({
      name: '',
      addressLine: '',
      postalCode: '',
      cityId: '',
      cityName: '',
      provinceName: '',
      countryName: '',
      isDefault: false,
      isActive: true,
    });
    setSelectedCountryId('');
    setSelectedProvinceId('');
  };

  const handleCountryChange = (val: string) => {
    setSelectedCountryId(val);
    setSelectedProvinceId('');
    setFormData(prev => ({ 
      ...prev, 
      cityId: '', 
      cityName: '', 
      provinceName: '', 
      countryName: countries.find(c => c.id === val)?.name || '' 
    }));
  };

  const handleProvinceChange = (val: string) => {
    setSelectedProvinceId(val);
    setFormData(prev => ({ 
      ...prev, 
      cityId: '', 
      cityName: '', 
      provinceName: provinces.find(p => p.id === val)?.name || '' 
    }));
  };

  const handleCityChange = (val: string) => {
    const city = cities.find(c => c.id === val);
    setFormData(prev => ({ 
      ...prev, 
      cityId: val,
      cityName: city?.name || '',
      // Ensure province and country names are captured if they weren't already
      provinceName: prev.provinceName || city?.provinceName || '',
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Critical to prevent bubbling to parent form
    
    setError(null);
    
    if (!formData.addressLine || !formData.postalCode || !formData.cityId) {
      setError('Por favor complete todos los campos obligatorios');
      return;
    }

    // Call onSave with the current address data
    onSave({
        ...address, // Keep ID if editing
        ...formData
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 flex flex-col max-w-4xl max-h-[90vh] sm:max-w-4xl">
        <DialogHeader className="p-6 border-b">
          <DialogTitle>{address ? 'Editar Dirección' : 'Nueva Dirección de Entrega'}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Form Side */}
            <form id="address-form" onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <AlertCircle className="text-red-600 h-5 w-5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-red-800 font-bold">Error</p>
                    <p className="text-red-600">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="addr-name">Nombre de la dirección (opcional)</Label>
                  <Input
                    id="addr-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Almacén Central, Tienda..."
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    data-form-type="other"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>País *</Label>
                    <LocationCombobox
                      options={countries}
                      value={selectedCountryId}
                      onValueChange={handleCountryChange}
                      placeholder="Seleccione país"
                      isLoading={loadingCountries}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Provincia *</Label>
                    <LocationCombobox
                      options={provinces}
                      value={selectedProvinceId}
                      onValueChange={handleProvinceChange}
                      placeholder="Seleccione provincia"
                      isLoading={loadingProvinces}
                      disabled={!selectedCountryId}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Municipio *</Label>
                    <LocationCombobox
                      options={cities}
                      value={formData.cityId}
                      onValueChange={handleCityChange}
                      placeholder="Seleccione municipio"
                      isLoading={loadingCities}
                      disabled={!selectedProvinceId}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="postalCode">Código Postal *</Label>
                    <Input
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                      placeholder="28001"
                      maxLength={5}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="addressLine">Dirección *</Label>
                  <Input
                    id="addressLine"
                    value={formData.addressLine}
                    onChange={(e) => setFormData({ ...formData, addressLine: e.target.value })}
                    placeholder="Calle, número, piso, etc."
                  />
                </div>

                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox 
                    id="isDefault" 
                    checked={formData.isDefault} 
                    onCheckedChange={(val) => setFormData({ ...formData, isDefault: !!val })}
                  />
                  <Label htmlFor="isDefault" className="cursor-pointer">Dirección predeterminada</Label>
                </div>
              </div>
            </form>

            {/* Map Side */}
            <div className="flex flex-col gap-3">
              <Label className="flex items-center gap-2 text-slate-900 font-bold uppercase tracking-wider text-xs">
                <MapIcon className="h-4 w-4 text-primary" />
                Vista previa del mapa
              </Label>
              <LocationMap 
                address={formData.addressLine}
                city={formData.cityName}
                province={formData.provinceName}
                country={formData.countryName}
                postalCode={formData.postalCode}
                height="100%"
                zoom={16}
              />
              <p className="text-[10px] text-slate-500 italic">
                * La ubicación real se basa en la dirección escrita. Verifique el marcador en el mapa.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="p-6 border-t bg-slate-50/30">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="address-form"
            disabled={!formData.cityId}
            className="bg-blue-600 hover:bg-blue-700 min-w-[120px]"
          >
            Aceptar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
